import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  calcularCanteiro,
  COMPOSICOES_CANTEIRO,
  linhasCanteiro,
  TABELAS_CANTEIRO,
  valorOutro,
  type Dimensao,
  type ItemOutros,
  type LinhaAvulsa,
  type LinhaCanteiro,
  type ParametrosCanteiro,
  type PrecoCodigo,
  type RespostaCanteiro,
  type TipoAvulso,
} from "../../../shared/canteiro";
import type { TipoComposicao } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves, useOrcamento } from "../api/consultas";
import { useAvisos } from "../components/Avisos";
import { useTermoComAtraso } from "../components/useTermoComAtraso";
import { Carregando, ErroCarregamento, formatarMoeda, formatarQuantidade, lerNumero, semAutocompletar, Tabela } from "../components/ui";
import { useIdOrcamento } from "./OrcamentoDetalhe";

const chaveCanteiro = (id: number) => ["orcamento", id, "canteiro"] as const;

/** Campo numérico no padrão BR que mantém o texto digitado (ex.: "2," durante a digitação). */
function Numero({
  rotulo,
  valor,
  onChange,
  unidade,
  largura = 80,
  desabilitado,
}: {
  rotulo: string;
  valor: number;
  onChange: (v: number) => void;
  unidade?: string;
  largura?: number;
  desabilitado?: boolean;
}) {
  const [texto, setTexto] = useState(formatarQuantidade(valor));
  return (
    <label>
      {rotulo}
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          type="text"
          inputMode="decimal"
          value={texto}
          disabled={desabilitado}
          style={{ width: largura }}
          {...semAutocompletar}
          onChange={(e) => {
            setTexto(e.target.value);
            onChange(lerNumero(e.target.value.replace(/\./g, "")));
          }}
          onBlur={() => setTexto(formatarQuantidade(valor))}
        />
        {unidade && <span className="note">{unidade}</span>}
      </span>
    </label>
  );
}

function Resultado({ rotulo, valor, unidade, destaque }: { rotulo: string; valor: number; unidade: string; destaque?: boolean }) {
  return (
    <div className="canteiro-resultado">
      <span>{rotulo}</span>
      <strong className={destaque ? "destaque" : undefined}>
        {formatarQuantidade(Math.round(valor * 100) / 100)} {unidade}
      </strong>
    </div>
  );
}

function Secao({ titulo, children, dica }: { titulo: string; children: React.ReactNode; dica?: string }) {
  return (
    <section className="card canteiro-secao">
      <h3>{titulo}</h3>
      {dica && <p className="note">{dica}</p>}
      {children}
    </section>
  );
}

function normalizarCodigo(codigo: string): string {
  const c = codigo.trim().toUpperCase();
  return c && !c.includes("/") ? `${c}/EMBASA` : c;
}

export function OrcamentoCanteiro() {
  const id = useIdOrcamento();
  const detalhe = useOrcamento(id);
  const consulta = useQuery({
    queryKey: chaveCanteiro(id),
    queryFn: () => api<RespostaCanteiro>(`/api/orcamentos/${id}/canteiro`),
    staleTime: Infinity,
  });

  if (detalhe.isLoading || consulta.isLoading) return <Carregando />;
  if (detalhe.error || consulta.error || !detalhe.data || !consulta.data) {
    return <ErroCarregamento erro={detalhe.error ?? consulta.error} />;
  }
  return (
    <EditorCanteiro
      key={consulta.dataUpdatedAt}
      orcamentoId={id}
      nome={detalhe.data.orcamento.nome}
      podeGerenciar={detalhe.data.pode_gerenciar}
      inicial={consulta.data}
    />
  );
}

function EditorCanteiro({
  orcamentoId,
  nome,
  podeGerenciar,
  inicial,
}: {
  orcamentoId: number;
  nome: string;
  podeGerenciar: boolean;
  inicial: RespostaCanteiro;
}) {
  const [p, setP] = useState<ParametrosCanteiro>(inicial.parametros);
  const [precos, setPrecos] = useState<Record<string, PrecoCodigo | undefined>>(inicial.precos);
  const queryClient = useQueryClient();
  const { avisar, avisarErro } = useAvisos();
  const m = calcularCanteiro(p);
  const linhas = linhasCanteiro(p, precos);
  const ro = !podeGerenciar;

  const alterar = (mudanca: (atual: ParametrosCanteiro) => ParametrosCanteiro) => setP((atual) => mudanca(structuredClone(atual)));

  async function carregarCotacao(codigo: string) {
    if (!codigo || codigo in precos) return;
    try {
      const achados = await api<Record<string, PrecoCodigo>>(
        `/api/orcamentos/${orcamentoId}/canteiro/cotacoes?${new URLSearchParams({ codigos: codigo })}`,
      );
      setPrecos((atual) => ({ ...atual, [codigo]: achados[codigo] }));
      if (!achados[codigo]) avisarErro(new Error(`Código ${codigo} não encontrado nas tabelas de preços/insumos deste orçamento.`));
    } catch (erro) {
      avisarErro(erro);
    }
  }

  const salvar = useMutation({
    mutationFn: () => api<{ mensagem: string }>(`/api/orcamentos/${orcamentoId}/canteiro`, { method: "PUT", body: { parametros: p } }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      queryClient.invalidateQueries({ queryKey: chaves.orcamento(orcamentoId) });
      queryClient.invalidateQueries({ queryKey: chaveCanteiro(orcamentoId) });
    },
    onError: avisarErro,
  });

  const dimensao = (rotulo: string, chave: "leves" | "grandes" | "caminhoes") => {
    const d: Dimensao = p.estacionamento[chave];
    const set = (campo: keyof Dimensao) => (v: number) =>
      alterar((x) => {
        x.estacionamento[chave][campo] = v;
        return x;
      });
    return (
      <tr>
        <td>{rotulo}</td>
        <td>
          <Numero rotulo="" valor={d.quantidade} onChange={set("quantidade")} largura={50} desabilitado={ro} />
        </td>
        <td>
          <Numero rotulo="" valor={d.largura} onChange={set("largura")} largura={60} desabilitado={ro} />
        </td>
        <td>
          <Numero rotulo="" valor={d.comprimento} onChange={set("comprimento")} largura={60} desabilitado={ro} />
        </td>
        <td className="num">{formatarQuantidade(Math.round(d.quantidade * d.largura * d.comprimento * 100) / 100)}</td>
      </tr>
    );
  };

  const alterarOutro = (i: number, mudanca: (o: ItemOutros) => void) =>
    alterar((x) => {
      mudanca(x.outros[i]);
      return x;
    });

  const listaDe = (x: ParametrosCanteiro, grupo: GrupoAvulso) => (grupo === "administracao_local" ? x.administracao.linhas : x.avulsos[grupo]);
  const edicaoAvulsos = (grupo: GrupoAvulso): EdicaoAvulsos => ({
    alterar: (i, mudanca) =>
      alterar((x) => {
        mudanca(listaDe(x, grupo)[i]);
        return x;
      }),
    remover: (i) =>
      alterar((x) => {
        listaDe(x, grupo).splice(i, 1);
        return x;
      }),
    trocarCodigo: (i, codigo) => {
      alterar((x) => {
        listaDe(x, grupo)[i].codigo = codigo;
        return x;
      });
      void carregarCotacao(codigo);
    },
    adicionar: (cotacao) => {
      if (cotacao) setPrecos((atual) => ({ ...atual, [cotacao.codigo]: cotacao }));
      alterar((x) => {
        listaDe(x, grupo).push({
          id: `${grupo}-${Date.now()}`,
          codigo: cotacao?.codigo ?? "",
          descricao: cotacao ? "" : "Item avulso",
          unidade: cotacao ? "" : "UN",
          quantidade: 1,
          multiplicador: null,
          cotacao: cotacao ? null : 0,
        });
        return x;
      });
    },
  });

  const faixaAtual = p.operarios < 31 ? 0 : p.operarios < 101 ? 1 : 2;
  const FAIXAS = ["Até 30 operários", "De 31 a 100 operários", "Acima de 100 operários"];
  const linhasAdm = linhas.filter((l) => l.tipo === "administracao_local");

  return (
    <>
      <p className="note">
        <Link to={`/orcamentos/${orcamentoId}`}>← voltar ao orçamento</Link>
      </p>
      <h1 style={{ marginBottom: 4 }}>Canteiro de obras — mobilização, desmobilização e administração local</h1>
      <p className="note">
        {nome} · Preencha a <strong>memória de cálculo</strong> (seções 1 a 7): os coeficientes das composições 01.91.00 e
        01.91.01 são recalculados na hora. Na <strong>administração local</strong> (01.99.01) informe o multiplicador e a equipe/despesas (coeficiente = quantidade × multiplicador; multiplicador em branco = padrão).
        Depois clique em <strong>Salvar e aplicar</strong>: as três composições entram no orçamento (linhas lançadas à mão
        nesses blocos são mantidas).
        {!inicial.salvo && " Valores iniciais iguais aos da composição de referência (35 operários) — ajuste para esta obra."}
      </p>

      <h2>Memória de cálculo</h2>
      <div className="canteiro-grade">
        <Secao titulo="Dados gerais">
          <div className="form-row">
            <Numero
              rotulo="Nº máx. de operários simultâneos"
              valor={p.operarios}
              unidade="un"
              desabilitado={ro}
              onChange={(v) => alterar((x) => ({ ...x, operarios: Math.round(v) }))}
            />
          </div>
          <Resultado rotulo="Largura do terreno" valor={m.terreno.largura} unidade="m" />
          <Resultado rotulo="Comprimento do terreno" valor={m.terreno.comprimento} unidade="m" />
          <p className="note">Dimensões calculadas pela área ocupada + estacionamento + silos (arredondadas de 5 em 5 m).</p>
        </Secao>

        <Secao titulo="1 - Preparação do terreno">
          <div className="form-row">
            <Numero rotulo="Altura da cerca" valor={p.cerca_altura} unidade="m" desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, cerca_altura: v }))} />
            <Numero rotulo="Largura do passeio" valor={p.passeio_largura} unidade="m" desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, passeio_largura: v }))} />
            <Numero rotulo="Altura da brita" valor={p.brita_altura} unidade="m" desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, brita_altura: v }))} />
          </div>
          <Resultado rotulo="Limpeza do terreno / regularização" valor={m.terreno.area} unidade="m²" destaque />
          <Resultado rotulo={`Cerca/muro (extensão ${formatarQuantidade(m.cerca.extensao)} m)`} valor={m.cerca.area} unidade="m²" destaque />
          <Resultado rotulo="Locação — área ocupada" valor={m.area_ocupada} unidade="m²" destaque />
          <Resultado rotulo={`Passeio (extensão ${formatarQuantidade(m.passeio.extensao)} m)`} valor={m.passeio.area} unidade="m²" destaque />
          <Resultado rotulo="Brita (terreno − área ocupada)" valor={m.brita.volume} unidade="m³" destaque />
        </Secao>

        <Secao titulo="Estacionamento" dica="Usado só no dimensionamento do terreno.">
          <Tabela>
            <thead>
              <tr>
                <th />
                <th>Quant.</th>
                <th>Largura (m)</th>
                <th>Compr. (m)</th>
                <th className="num">Total (m²)</th>
              </tr>
            </thead>
            <tbody>
              {dimensao("Veículos leves", "leves")}
              {dimensao("Veículos grandes", "grandes")}
              {dimensao("Caminhões / retro", "caminhoes")}
              <tr>
                <td>Faixa de acesso</td>
                <td>
                  <Numero rotulo="" valor={p.estacionamento.faixa_quantidade} largura={50} desabilitado={ro} onChange={(v) => alterar((x) => { x.estacionamento.faixa_quantidade = v; return x; })} />
                </td>
                <td>
                  <Numero rotulo="" valor={p.estacionamento.faixa_largura} largura={60} desabilitado={ro} onChange={(v) => alterar((x) => { x.estacionamento.faixa_largura = v; return x; })} />
                </td>
                <td className="num">{formatarQuantidade(m.estacionamento.faixa_comprimento)}</td>
                <td className="num">{formatarQuantidade(Math.round(m.estacionamento.faixa * 100) / 100)}</td>
              </tr>
            </tbody>
          </Tabela>
          <Resultado rotulo="Área considerada" valor={m.estacionamento.total} unidade="m²" />
        </Secao>

        <Secao
          titulo="2 - Instalações sanitárias / refeitório"
          dica="Conjunto lavatório, vaso e mictório a cada grupo de trabalhadores ou fração; chuveiro, vestiário e refeitório pela área mínima."
        >
          <div className="form-row">
            <Numero rotulo="Operários por conjunto" valor={p.operarios_por_conjunto} desabilitado={ro} largura={60} onChange={(v) => alterar((x) => ({ ...x, operarios_por_conjunto: v }))} />
            <Numero rotulo="Operários por chuveiro" valor={p.operarios_por_chuveiro} desabilitado={ro} largura={60} onChange={(v) => alterar((x) => ({ ...x, operarios_por_chuveiro: v }))} />
          </div>
          <div className="form-row">
            {(
              [
                ["lavatorio", "Lavatório"],
                ["vaso", "Vaso sanitário"],
                ["mictorio", "Mictório"],
                ["chuveiro", "Chuveiro"],
                ["vestiario", "Vestiário (por pessoa)"],
                ["refeitorio", "Refeitório (por pessoa)"],
              ] as const
            ).map(([chave, rotulo]) => (
              <Numero
                key={chave}
                rotulo={`${rotulo} — área mín.`}
                valor={p.areas_minimas[chave]}
                unidade="m²"
                largura={55}
                desabilitado={ro}
                onChange={(v) => alterar((x) => { x.areas_minimas[chave] = v; return x; })}
              />
            ))}
          </div>
          <Resultado rotulo={`Sanitários (${m.sanitarios.conjuntos} conjunto(s), ${m.sanitarios.chuveiros} chuveiro(s))`} valor={m.sanitarios.area_sanitarios} unidade="m²" destaque />
          <Resultado rotulo="Vestiário" valor={m.sanitarios.vestiario} unidade="m²" destaque />
          <Resultado rotulo="Refeitório" valor={m.sanitarios.refeitorio} unidade="m²" destaque />
        </Secao>

        <Secao titulo="3 - Ambulatório" dica="Frentes de trabalho com 50 ou mais trabalhadores: 1 sala de 2,20 × 5,00 m (até 100) ou 2 salas (acima de 100).">
          <Resultado rotulo="Área considerada" valor={m.ambulatorio} unidade="m²" destaque />
        </Secao>

        <Secao titulo="4 - Escritórios / cômodos" dica="Área pela faixa de operários (em destaque a faixa desta obra).">
          <Tabela>
            <thead>
              <tr>
                <th>Faixa</th>
                <th className="num">Contratada</th>
                <th className="num">Almox./escritório</th>
                <th className="num">Depósito cimento</th>
              </tr>
            </thead>
            <tbody>
              {FAIXAS.map((rotulo, i) => (
                <tr key={rotulo} className={i === faixaAtual ? "canteiro-faixa-atual" : undefined}>
                  <td>{rotulo}</td>
                  <td className="num">{TABELAS_CANTEIRO.comodos.contratada[i]} m²</td>
                  <td className="num">{TABELAS_CANTEIRO.comodos.almoxarifado[i]} m²</td>
                  <td className="num">{TABELAS_CANTEIRO.comodos.deposito_cimento[i]} m²</td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Secao>

        <Secao titulo="5 - Centrais, silos e caixa d'água">
          <div className="form-row">
            <label style={{ flexDirection: "row", alignItems: "center" }}>
              <input type="checkbox" checked={p.centrais.forma} disabled={ro} onChange={(e) => alterar((x) => { x.centrais.forma = e.target.checked; return x; })} />
              Considerar central de forma ({TABELAS_CANTEIRO.centrais.forma[faixaAtual]} m²)
            </label>
            <label style={{ flexDirection: "row", alignItems: "center" }}>
              <input type="checkbox" checked={p.centrais.aco} disabled={ro} onChange={(e) => alterar((x) => { x.centrais.aco = e.target.checked; return x; })} />
              Considerar central de aço ({TABELAS_CANTEIRO.centrais.aco[faixaAtual]} m²)
            </label>
          </div>
          <Resultado rotulo={`Silos (areia ${m.silos.areia}, solo ${m.silos.solo}, brita ${m.silos.brita})`} valor={m.silos.total} unidade="un" destaque />
          <Resultado rotulo="Caixa d'água 1.000 L (70 L por operário)" valor={m.silos.caixa_dagua} unidade="un" destaque />
        </Secao>

        <Secao titulo="6 - Desmobilização">
          <div className="form-row">
            <Numero rotulo="Espessura — piso" valor={p.espessuras_entulho.piso} unidade="m" largura={55} desabilitado={ro} onChange={(v) => alterar((x) => { x.espessuras_entulho.piso = v; return x; })} />
            <Numero rotulo="Espessura — cobertura" valor={p.espessuras_entulho.cobertura} unidade="m" largura={55} desabilitado={ro} onChange={(v) => alterar((x) => { x.espessuras_entulho.cobertura = v; return x; })} />
            <Numero rotulo="Espessura — tapume" valor={p.espessuras_entulho.tapume} unidade="m" largura={55} desabilitado={ro} onChange={(v) => alterar((x) => { x.espessuras_entulho.tapume = v; return x; })} />
            <Numero rotulo="DMT do bota-fora" valor={p.dmt_km} unidade="km" largura={55} desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, dmt_km: v }))} />
            <Numero rotulo="Grama (% do terreno)" valor={p.grama_percentual * 100} unidade="%" largura={55} desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, grama_percentual: v / 100 }))} />
            <Numero rotulo="1 árvore a cada" valor={p.m2_por_arvore} unidade="m²" largura={60} desabilitado={ro} onChange={(v) => alterar((x) => ({ ...x, m2_por_arvore: v }))} />
          </div>
          <Resultado rotulo="Demolição de piso / cobertura" valor={m.desmobilizacao.demolicao.total} unidade="m²" destaque />
          <Resultado rotulo="Retirada de tapume e barracões" valor={m.desmobilizacao.retirada.total} unidade="m²" destaque />
          <Resultado rotulo="Carga manual de entulho" valor={m.desmobilizacao.entulho.total} unidade="m³" destaque />
          <Resultado rotulo="Transporte de entulho" valor={m.desmobilizacao.transporte.momento} unidade="m³·km" destaque />
          <Resultado rotulo="Regularização do terreno" valor={m.desmobilizacao.regularizacao} unidade="m²" destaque />
          <Resultado rotulo="Plantio de grama" valor={m.desmobilizacao.grama} unidade="m²" destaque />
          <Resultado rotulo="Plantio de árvores" valor={m.desmobilizacao.arvores} unidade="un" destaque />
        </Secao>
      </div>

      <Secao
        titulo="7 - Outros"
        dica="Itens que não estão nas seções acima (ex.: guarita, container, área de vivência). Resultado = quant. × comprimento × largura × altura (campo vazio não entra na conta). Escolha a composição para levar o item; com código EMBASA a cotação vem da tabela, sem código digite a cotação."
      >
        {p.outros.length > 0 && (
          <Tabela>
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="num">Quant.</th>
                <th className="num">Compr. (m)</th>
                <th className="num">Largura (m)</th>
                <th className="num">Altura (m)</th>
                <th>Un</th>
                <th className="num">Resultado</th>
                <th>Levar para</th>
                <th>Código EMBASA</th>
                <th className="num">Cotação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {p.outros.map((o, i) => {
                const preco = o.codigo ? precos[o.codigo] : undefined;
                return (
                  <tr key={o.id}>
                    <td>
                      <input type="text" value={o.descricao} disabled={ro} placeholder="descrição do item" style={{ minWidth: 170, width: "100%" }} {...semAutocompletar} onChange={(e) => alterarOutro(i, (x) => { x.descricao = e.target.value; })} />
                    </td>
                    <td className="num">
                      <Numero rotulo="" valor={o.quantidade} largura={55} desabilitado={ro} onChange={(v) => alterarOutro(i, (x) => { x.quantidade = v; })} />
                    </td>
                    {(["comprimento", "largura", "altura"] as const).map((campo) => (
                      <td key={campo} className="num">
                        <NumeroOpcional valor={o[campo]} placeholder="—" desabilitado={ro} onChange={(v) => alterarOutro(i, (x) => { x[campo] = v; })} />
                      </td>
                    ))}
                    <td>
                      <input type="text" value={o.unidade} disabled={ro} placeholder={preco?.unidade ?? "UN"} style={{ width: 60 }} {...semAutocompletar} onChange={(e) => alterarOutro(i, (x) => { x.unidade = e.target.value; })} />
                    </td>
                    <td className="num">
                      <strong>{formatarQuantidade(Math.round(valorOutro(o) * 1000) / 1000)}</strong>
                    </td>
                    <td>
                      <select value={o.composicao ?? ""} disabled={ro} onChange={(e) => alterarOutro(i, (x) => { x.composicao = (e.target.value || null) as TipoAvulso | null; })}>
                        <option value="">só memória</option>
                        <option value="mobilizacao">Mobilização</option>
                        <option value="desmobilizacao">Desmobilização</option>
                      </select>
                    </td>
                    <td>
                      <input
                        key={o.codigo}
                        type="text"
                        defaultValue={o.codigo}
                        disabled={ro}
                        placeholder="sem código"
                        style={{ width: 165 }}
                        {...semAutocompletar}
                        onBlur={(e) => {
                          const codigo = normalizarCodigo(e.target.value);
                          if (codigo === o.codigo) return;
                          alterarOutro(i, (x) => { x.codigo = codigo; });
                          void carregarCotacao(codigo);
                        }}
                      />
                      {o.codigo && (
                        <>
                          <br />
                          <span className="note">{preco?.descricao ?? "código sem cotação nesta tabela"}</span>
                        </>
                      )}
                    </td>
                    <td className="num">
                      <NumeroOpcional valor={o.cotacao} placeholder={preco ? formatarMoeda(preco.preco_unitario) : "0,00"} largura={95} desabilitado={ro} onChange={(v) => alterarOutro(i, (x) => { x.cotacao = v; })} />
                    </td>
                    <td>
                      {!ro && (
                        <button type="button" className="btn-danger" onClick={() => alterar((x) => { x.outros.splice(i, 1); return x; })}>
                          remover
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
        {!ro && (
          <button
            type="button"
            className="btn-ghost"
            style={{ marginTop: 10 }}
            onClick={() =>
              alterar((x) => {
                x.outros.push({ id: `outro-${Date.now()}`, descricao: "", unidade: "M2", quantidade: 1, comprimento: null, largura: null, altura: null, composicao: "mobilizacao", codigo: "", cotacao: null });
                return x;
              })
            }
          >
            + adicionar item em 7 - Outros
          </button>
        )}
      </Secao>


      <h2>Composições</h2>
      <p className="note">
        Coeficiente em branco = memória de cálculo; digite um valor para substituir. Troque o código de qualquer linha (a
        unidade deve ser a mesma). Desmarque para não levar a linha. Cotações da tabela deste orçamento, sem BDI (o BDI é
        aplicado no resumo financeiro).
      </p>
      {(["mobilizacao", "desmobilizacao"] as const).map((tipo) => (
        <TabelaComposicao
          key={tipo}
          tipo={tipo}
          linhas={linhas.filter((l) => l.tipo === tipo && !l.chave.startsWith("avulso:"))}
          avulsas={linhas.filter((l) => l.tipo === tipo && l.chave.startsWith("avulso:"))}
          somenteLeitura={ro}
          onIncluir={(chave, incluir) =>
            alterar((x) => ({ ...x, excluidas: incluir ? x.excluidas.filter((c) => c !== chave) : [...x.excluidas, chave] }))
          }
          onCodigo={(chave, codigo) => {
            if (chave.startsWith("outro:")) {
              const id = chave.slice("outro:".length);
              alterar((x) => ({ ...x, outros: x.outros.map((o) => (o.id === id ? { ...o, codigo } : o)) }));
            } else {
              alterar((x) => ({ ...x, codigos: { ...x.codigos, [chave]: codigo } }));
            }
            void carregarCotacao(codigo);
          }}
          onCoeficiente={(chave, valor) =>
            alterar((x) => {
              const coeficientes = { ...x.coeficientes };
              if (valor === null) delete coeficientes[chave];
              else coeficientes[chave] = valor;
              return { ...x, coeficientes };
            })
          }
        >
          <ItensAvulsos
            titulo="Itens avulsos"
            orcamentoId={orcamentoId}
            linhas={p.avulsos[tipo]}
            calculadas={linhas.filter((l) => l.tipo === tipo && l.chave.startsWith("avulso:"))}
            multiplicadorPadrao={1}
            somenteLeitura={ro}
            edicao={edicaoAvulsos(tipo)}
          />
        </TabelaComposicao>
      ))}

      <div className="card">
        <CabecalhoComposicao tipo="administracao_local" total={linhasAdm.filter((l) => l.incluida).reduce((s, l) => s + l.total, 0)} />
        <div className="form-row">
          <Numero
            rotulo="Multiplicador padrão"
            valor={p.administracao.multiplicador_padrao}
            largura={60}
            desabilitado={ro}
            onChange={(v) => alterar((x) => { x.administracao.multiplicador_padrao = v; return x; })}
          />
          <span className="note" style={{ alignSelf: "center" }}>
            Mão de obra (M.O.): cotação = preço do insumo × (1 + L.S. mensalista
            {inicial.leis_sociais_mensalista !== null ? ` ${formatarQuantidade(inicial.leis_sociais_mensalista)}%` : ""} da tabela deste orçamento).
          </span>
          {p.administracao.multiplicador_padrao <= 0 && <span className="badge badge-warn">informe o multiplicador padrão</span>}
        </div>
        <ItensAvulsos
          orcamentoId={orcamentoId}
          linhas={p.administracao.linhas}
          calculadas={linhasAdm}
          multiplicadorPadrao={p.administracao.multiplicador_padrao}
          somenteLeitura={ro}
          edicao={edicaoAvulsos("administracao_local")}
        />
      </div>

      {podeGerenciar && (
        <div className="canteiro-acoes">
          <span>
            Total das três composições (sem BDI): <strong>R$ {formatarMoeda(linhas.filter((l) => l.incluida).reduce((s, l) => s + l.total, 0))}</strong>
          </span>
          <button type="button" className="btn-orange" disabled={salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? "Aplicando…" : "Salvar e aplicar no orçamento"}
          </button>
        </div>
      )}
    </>
  );
}

function CabecalhoComposicao({ tipo, total }: { tipo: TipoComposicao; total: number }) {
  const c = COMPOSICOES_CANTEIRO[tipo];
  return (
    <div className="canteiro-cabecalho-composicao">
      <span>
        <strong>{c.codigo.replace("/EMBASA", "")}</strong> — {c.descricao} <span className="note">(UN)</span>
      </span>
      <strong>R$ {formatarMoeda(total)}</strong>
    </div>
  );
}

function TabelaComposicao({
  tipo,
  linhas,
  avulsas,
  somenteLeitura,
  onIncluir,
  onCodigo,
  onCoeficiente,
  children,
}: {
  tipo: TipoComposicao;
  linhas: LinhaCanteiro[];
  avulsas: LinhaCanteiro[];
  somenteLeitura: boolean;
  onIncluir: (chave: string, incluir: boolean) => void;
  onCodigo: (chave: string, codigo: string) => void;
  onCoeficiente: (chave: string, valor: number | null) => void;
  children: React.ReactNode;
}) {
  const total = [...linhas, ...avulsas].filter((l) => l.incluida).reduce((s, l) => s + l.total, 0);
  return (
    <div className="card">
      <CabecalhoComposicao tipo={tipo} total={total} />
      <Tabela>
        <thead>
          <tr>
            <th />
            <th>Código</th>
            <th>Descrição do insumo ou serviço</th>
            <th>Tipo</th>
            <th>Un</th>
            <th className="num">Coef.</th>
            <th className="num">Cotação</th>
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const manual = l.coeficiente !== l.coeficiente_calculado;
            return (
              <tr key={l.chave} className={l.incluida ? undefined : "canteiro-linha-fora"}>
                <td>
                  <input
                    type="checkbox"
                    checked={l.incluida}
                    disabled={somenteLeitura || l.coeficiente <= 0}
                    title={l.coeficiente <= 0 ? "Coeficiente zero pela memória de cálculo" : "Levar para a composição"}
                    onChange={(e) => onIncluir(l.chave, e.target.checked)}
                  />
                </td>
                <td>
                  {somenteLeitura ? (
                    l.codigo
                  ) : (
                    <input
                      key={l.codigo}
                      type="text"
                      defaultValue={l.codigo}
                      style={{ width: 165 }}
                      {...semAutocompletar}
                      onBlur={(e) => {
                        const codigo = normalizarCodigo(e.target.value);
                        if (codigo && codigo !== l.codigo) onCodigo(l.chave, codigo);
                      }}
                    />
                  )}
                </td>
                <td>
                  {l.descricao}
                  <br />
                  <span className="note">{l.encontrado ? l.memoria : "código sem cotação nesta tabela"}</span>
                </td>
                <td>{l.tipo_codigo}</td>
                <td>{l.unidade ?? "—"}</td>
                <td className="num">
                  {somenteLeitura ? (
                    formatarQuantidade(l.coeficiente)
                  ) : (
                    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                      <input
                        key={`${l.coeficiente}`}
                        type="text"
                        inputMode="decimal"
                        defaultValue={formatarQuantidade(Math.round(l.coeficiente * 1000) / 1000)}
                        className={manual ? "canteiro-coef-manual" : undefined}
                        style={{ width: 80, textAlign: "right" }}
                        {...semAutocompletar}
                        onBlur={(e) => {
                          const texto = e.target.value.trim();
                          if (!texto) return onCoeficiente(l.chave, null);
                          const valor = lerNumero(texto.replace(/\./g, ""), -1);
                          if (valor >= 0 && Math.abs(valor - l.coeficiente) > 1e-9) onCoeficiente(l.chave, valor);
                        }}
                      />
                      {manual && (
                        <button type="button" className="btn-ghost canteiro-reset" title="Voltar ao valor da memória de cálculo" onClick={() => onCoeficiente(l.chave, null)}>
                          ↺
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td className="num">{formatarMoeda(l.cotacao)}</td>
                <td className="num">{formatarMoeda(l.total)}</td>
              </tr>
            );
          })}
        </tbody>
      </Tabela>
      {children}
    </div>
  );
}

type GrupoAvulso = TipoAvulso | "administracao_local";

interface EdicaoAvulsos {
  alterar: (i: number, mudanca: (l: LinhaAvulsa) => void) => void;
  remover: (i: number) => void;
  trocarCodigo: (i: number, codigo: string) => void;
  /** Sem cotação = linha livre (descrição, unidade e cotação digitadas). */
  adicionar: (cotacao?: PrecoCodigo) => void;
}

/** Número opcional no padrão BR: vazio = null (usa o valor indicado no placeholder). */
function NumeroOpcional({
  valor,
  placeholder,
  onChange,
  largura = 60,
  desabilitado,
}: {
  valor: number | null;
  placeholder: string;
  onChange: (v: number | null) => void;
  largura?: number;
  desabilitado?: boolean;
}) {
  const [texto, setTexto] = useState(valor === null ? "" : formatarQuantidade(valor));
  return (
    <input
      type="text"
      inputMode="decimal"
      value={texto}
      placeholder={placeholder}
      disabled={desabilitado}
      style={{ width: largura, textAlign: "right" }}
      {...semAutocompletar}
      onChange={(e) => {
        setTexto(e.target.value);
        const t = e.target.value.trim();
        onChange(t === "" ? null : lerNumero(t.replace(/\./g, "")));
      }}
    />
  );
}

/** Linhas digitadas de uma composição: equipe/despesas da adm. local ou itens avulsos de mob./desmob. */
function ItensAvulsos({
  titulo,
  orcamentoId,
  linhas,
  calculadas,
  multiplicadorPadrao,
  somenteLeitura: ro,
  edicao,
}: {
  titulo?: string;
  orcamentoId: number;
  linhas: LinhaAvulsa[];
  calculadas: LinhaCanteiro[];
  multiplicadorPadrao: number;
  somenteLeitura: boolean;
  edicao: EdicaoAvulsos;
}) {
  return (
    <div className="canteiro-avulsos">
      {titulo && <h4>{titulo}</h4>}
      {linhas.length > 0 && (
        <Tabela>
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição do insumo ou serviço</th>
              <th>Tipo</th>
              <th>Un</th>
              <th className="num">Quant.</th>
              <th className="num">Multiplicador</th>
              <th className="num">Coef.</th>
              <th className="num">Cotação</th>
              <th className="num">Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => {
              const calc = calculadas[i];
              const livre = !l.codigo;
              return (
                <tr key={l.id} className={calc.incluida ? undefined : "canteiro-linha-fora"}>
                  <td>
                    {ro ? (
                      l.codigo || "—"
                    ) : (
                      <input
                        key={l.codigo}
                        type="text"
                        defaultValue={l.codigo}
                        placeholder="sem código"
                        style={{ width: 165 }}
                        {...semAutocompletar}
                        onBlur={(e) => {
                          const codigo = normalizarCodigo(e.target.value);
                          if (codigo !== l.codigo) edicao.trocarCodigo(i, codigo);
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {ro ? (
                      calc.descricao
                    ) : (
                      <input
                        type="text"
                        value={l.descricao}
                        placeholder={calc.descricao}
                        style={{ width: "100%", minWidth: 260 }}
                        {...semAutocompletar}
                        onChange={(e) => edicao.alterar(i, (x) => { x.descricao = e.target.value; })}
                      />
                    )}
                    <br />
                    <span className="note">
                      {calc.encontrado ? calc.memoria : l.codigo ? "código sem cotação nesta tabela — digite a cotação" : "linha livre — digite a cotação"}
                    </span>
                  </td>
                  <td>{livre ? "—" : calc.tipo_codigo}</td>
                  <td>
                    {ro ? (
                      calc.unidade ?? "—"
                    ) : (
                      <input
                        type="text"
                        value={l.unidade}
                        placeholder={calc.unidade ?? "UN"}
                        style={{ width: 60 }}
                        {...semAutocompletar}
                        onChange={(e) => edicao.alterar(i, (x) => { x.unidade = e.target.value; })}
                      />
                    )}
                  </td>
                  <td className="num">
                    <Numero rotulo="" valor={l.quantidade} largura={55} desabilitado={ro} onChange={(v) => edicao.alterar(i, (x) => { x.quantidade = v; })} />
                  </td>
                  <td className="num">
                    <NumeroOpcional
                      valor={l.multiplicador}
                      placeholder={formatarQuantidade(multiplicadorPadrao)}
                      desabilitado={ro}
                      onChange={(v) => edicao.alterar(i, (x) => { x.multiplicador = v; })}
                    />
                  </td>
                  <td className="num">{formatarQuantidade(Math.round(calc.coeficiente * 1000) / 1000)}</td>
                  <td className="num">
                    <NumeroOpcional
                      valor={l.cotacao}
                      placeholder={formatarMoeda(calc.cotacao)}
                      largura={95}
                      desabilitado={ro}
                      onChange={(v) => edicao.alterar(i, (x) => { x.cotacao = v; })}
                    />
                  </td>
                  <td className="num">{formatarMoeda(calc.total)}</td>
                  <td>
                    {!ro && (
                      <button type="button" className="btn-danger" onClick={() => edicao.remover(i)}>
                        remover
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Tabela>
      )}
      {!ro && <BuscaItemAvulso orcamentoId={orcamentoId} onEscolher={edicao.adicionar} onLivre={() => edicao.adicionar()} />}
    </div>
  );
}

function BuscaItemAvulso({
  orcamentoId,
  onEscolher,
  onLivre,
}: {
  orcamentoId: number;
  onEscolher: (cotacao: PrecoCodigo) => void;
  onLivre: () => void;
}) {
  const [q, setQ] = useState("");
  const termo = useTermoComAtraso(q);
  const { data: achados = [], isFetching } = useQuery({
    queryKey: ["canteiro-buscar", orcamentoId, termo],
    queryFn: () => api<PrecoCodigo[]>(`/api/orcamentos/${orcamentoId}/canteiro/buscar?${new URLSearchParams({ q: termo })}`),
    enabled: termo.length >= 2,
  });
  return (
    <div style={{ marginTop: 10 }}>
      <div className="form-row">
        <label>
          Adicionar item avulso — serviço (tabela de preços) ou insumo
          <input
            type="search"
            placeholder="código ou descrição, ex.: 55.04.09, guarita, conta de energia..."
            style={{ minWidth: 360 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            {...semAutocompletar}
          />
        </label>
        <button type="button" className="btn-ghost" onClick={onLivre} title="Linha sem código EMBASA: descrição, unidade e cotação digitadas">
          + linha livre
        </button>
        {isFetching && <span className="note">Buscando…</span>}
      </div>
      {termo.length >= 2 && !isFetching && achados.length === 0 && <p className="note">Nada encontrado — use “+ linha livre”.</p>}
      {termo.length >= 2 && achados.length > 0 && (
        <Tabela>
          <tbody>
            {achados.map((a) => (
              <tr key={`${a.tipo}-${a.codigo}`}>
                <td>{a.codigo}</td>
                <td>{a.descricao}</td>
                <td>{a.tipo === "C" ? "serviço" : `insumo ${a.classe ?? ""}`}</td>
                <td>{a.unidade}</td>
                <td className="num">{formatarMoeda(a.preco_unitario)}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => {
                      onEscolher(a);
                      setQ("");
                    }}
                  >
                    + Add
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </div>
  );
}
